'use client';
import { Camera, Upload, User, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { type NormalizedError, normalizeError } from '../../lib/normalize-error';
import { useToastContext } from '../shared/Toast/ToastProvider';
import { Button } from '../ui/button';

type AvatarUploadProps = {
    currentAvatar?: string;
    onAvatarChange: (avatar: string | null) => void;
};
export function AvatarUpload({ currentAvatar, onAvatarChange }: AvatarUploadProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [preview, setPreview] = useState<string | null>(currentAvatar || null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToastContext();
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast({
                title: 'Invalid file type',
                description: 'Please select a valid image file (JPEG, PNG, GIF, or WebP).',
                variant: 'destructive'
            });
            return;
        }
        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            toast({
                title: 'File too large',
                description: `Please select an image smaller than 5MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`,
                variant: 'destructive'
            });
            return;
        }
        // Validate minimum file size (at least 1KB)
        if (file.size < 1024) {
            toast({
                title: 'File too small',
                description: 'Please select a valid image file.',
                variant: 'destructive'
            });
            return;
        }
        setIsUploading(true);
        try {
            // Create FormData
            const formData = new FormData();
            formData.append('avatar', file);
            // Upload to API
            const response = await fetch('/api/profile/avatar', {
                method: 'POST',
                body: formData,
            });
            const result = await response.json();
            if (response.ok) {
                // Update preview
                const reader = new FileReader();
                reader.onload = (e) => {
                    const result = e.target?.result as string;
                    setPreview(result);
                    onAvatarChange(result);
                };
                reader.readAsDataURL(file);
                toast({
                    title: 'Avatar updated',
                    description: 'Your profile picture has been updated successfully.',
                });
            }
            else {
                const errorMessage = result.error || 'Failed to upload avatar';
                throw new Error(errorMessage);
            }
        }
        catch (error: unknown) {
            const errorNormalized: NormalizedError = normalizeError(error);
            toast({
                title: 'Upload failed',
                description: errorNormalized.message,
                variant: 'destructive'
            });
        }
        finally {
            setIsUploading(false);
        }
    };
    const handleRemoveAvatar = async () => {
        try {
            const response = await fetch('/api/profile/avatar', {
                method: 'DELETE',
            });
            if (response.ok) {
                setPreview(null);
                onAvatarChange(null);
                toast({
                    title: 'Avatar removed',
                    description: 'Your profile picture has been removed.',
                });
            }
            else {
                throw new Error('Failed to remove avatar');
            }
        }
        catch {
            toast({
                title: 'Remove failed',
                description: 'Failed to remove avatar',
                variant: 'destructive'
            });
        }
    };
    const handleClick = () => {
        fileInputRef.current?.click();
    };
    return (<div className="flex flex-col items-center">
      <div className="relative">
        <div className="w-24 h-24 rounded-full bg-background-muted flex items-center justify-center overflow-hidden">
          {preview ? (<img src={preview} alt="Profile" className="w-full h-full object-cover"/>) : (<User className="w-12 h-12 text-foreground-muted"/>)}
        </div>
        
        {/* Upload overlay */}
        <button onClick={handleClick} disabled={isUploading} className="absolute inset-0 w-full h-full bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity disabled:opacity-50">
          {isUploading ? (<div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"/>) : (<Camera className="w-6 h-6 text-white"/>)}
        </button>
      </div>

      <div className="mt-3 flex space-x-2">
        <Button type="button" variant="outline" size="sm" onClick={handleClick} disabled={isUploading}>
          <Upload className="w-4 h-4 mr-1"/>
          {preview ? 'Change' : 'Upload'}
        </Button>
        
        {preview && (<Button type="button" variant="outline" size="sm" onClick={() => void handleRemoveAvatar()} disabled={isUploading}>
            <X className="w-4 h-4 mr-1"/>
            Remove
          </Button>)}
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={(event) => void handleFileSelect(event)} className="hidden"/>

      <p className="text-xs text-foreground-muted mt-1 text-center">
        JPG, PNG, GIF, or WebP. Max 5MB.
      </p>
      
      {isUploading && (<div className="mt-2 text-xs text-primary-default text-center">
          Uploading...
        </div>)}
    </div>);
}
